import {PaginatedBaseResponse, Response} from './api';
import {AppType} from './app';

export type TAppsQuery = {
  page: number;
  search?: string | Array<string>;
  category?: string;
  isFromClient?: boolean;
  appType?: AppType;
};

export type TFindAppsQuery = {
  page: number;
  query: string;
  isFromClient?: boolean;
  deviceId?: string;
  searchExps?: string;
};

export enum Orientation {
  PORTRAIT = 'PORTRAIT',
  LANDSCAPE = 'LANDSCAPE',
}

export enum AppState {
  ACTIVE = 'ACTIVE',
}

export enum ScreenshotType {
  SCREENSHOT = 'SCREENSHOT',
  TABLET_SCREENSHOT = 'TABLET_SCREENSHOT',
  LEGACY = 'image',
}

export enum AppStatus {
  PUBLISHED = 'PUBLISHED',
  HIDDEN = 'HIDDEN',
  NOT_PUBLISHED = 'NOT_PUBLISHED',
  BANNED = 'BANNED',
}

export type FileUrl = {
  fileUrl: string;
  fileUrlXs?: string; // img proxied url to smaller icon, NOT A REAL FIELD FROM BACKEND
  type: string;
  orientation?: Orientation;
  ordinal: number;
};

type PaginatedResponse<T> = Response<
  {
    content: Array<T>;
  } & PaginatedBaseResponse
>;

export type AggregatorInfo = {
  companyName: string | null;
  aggregatorAppId: number;
  source: string | null;
};

export type Marking = {
  id: number;
  name: string;
  description: string;
  imageUrl: string | null;
};

export type SeoTag = {
  id: number;
  label: string;
  appType: AppType;
};

export type App = {
  appId: number;
  activeVerCode: number;
  ageLegal: string;
  appStatus: AppStatus;
  appName: string;
  appType: AppType;
  category: string;
  categories: Array<string>;
  appVerUpdatedAt: string;
  companyName: string;
  companyStatus: string | null;
  companyLegalForm: string | null;
  downloads: number;
  fileSize: number;
  fileUrls: Array<FileUrl>;
  fullDescription: string | null;
  iconUrl: string;
  iconUrlXs?: string; // img proxied url to smaller icon, NOT A REAL FIELD FROM BACKEND
  id: number;
  ownerVkId: string;
  publicCompanyId: string;
  packageName: string;
  shortDescription: string;
  price: number;
  signature: string | null;
  state: AppState;
  createdAt: string | null;
  updatedAt: string | null;
  versionName: string;
  versionCode: number;
  whatsNew: string;
  moderInfo: string | null;
  minSdkVersion: number;
  targetSdkVersion: number;
  maxSdkVersion: number | null;
  website: string | null;
  privacyDataCategories: Array<string>;
  adaptedToTablets: boolean;
  purchased: boolean | null;
  inAppUpdatePriority: number;
  banner: string | null;
  aggregatorInfo?: AggregatorInfo | null;
  markings?: Array<Marking> | null;
  seoTags?: Array<SeoTag> | null;
  labelIds?: Array<number>;
  video?: VideoType;
  preorderStatus?: null | 'AVAILABLE';
  averageUserRating?: number;
  totalRatings?: number;
};

export type AppStub = Pick<
  App,
  | 'packageName'
  | 'appName'
  | 'fullDescription'
  | 'fileUrls'
  | 'iconUrl'
  | 'iconUrlXs'
  | 'createdAt'
  | 'updatedAt'
  | 'appType'
  | 'appStatus'
  | 'video'
> & {stub: true};

export const isStubAppData = (app: App | AppStub): app is AppStub =>
  'stub' in app;

export const isRealAppData = (app: App | AppStub): app is App => 'appId' in app;

export type AppSimilar = {
  appId: number;
  appName: string;
  appType: AppType;
  averageUserRating: number;
  packageName: string;
  shortDescription: string;
  iconUrl: string;
  versionCode: number;
  screenshotFileUrls: Array<FileUrl>;
  price: number;
  totalRatings: number;
};

export type AppResponse = Response<App>;
export type AppsResponse = PaginatedResponse<App>;
export type DeveloperAppsResponse = Response<{
  elements: Array<App>;
  elementsCount: number;
}>;

export type CompanyInfoResponse = {
  name: string;
};

export type AppsSimilarResponse = PaginatedResponse<AppSimilar>;

export type SearchAppsResponse = Response<
  {
    content: Array<App>;
    searchQueryId: string;
  } & PaginatedBaseResponse
>;

export type AppShortInfo = {
  appId: number;
  appName: string;
  packageName: string;
  shortDescription: string;
  iconUrl: string;
  averageUserRating: number;
};

export type AppShortInfoResponse = Response<{content: Array<AppShortInfo>}>;

/** рекомендации могут браться из разных источников, это обобщенные типы для них */
export type RecommendedApp = {
  appId: number;
  appName: string;
  packageName: string;
  shortDescription: string;
  iconUrl: string;
  appType: AppType;
  averageUserRating?: number | null;
  totalRatings?: number | null;
  price?: number;
  source?: string;
  preorderStatus?: null | 'AVAILABLE';
};

export type SelectionApp = RecommendedApp & {
  screenshots: FileUrl[];
};

export type ScreenshotsType = {
  fileUrl: string;
  orientation: Orientation;
};

export type RecommendedAppsData = {
  apps: Array<RecommendedApp>;
  hasMore: boolean;
  rec_source: string;
};

export type AppsSelectionContent = {
  contentId: string;
} & (
  | {
      type: 'APPS';
      title?: string;
      appInfo: Array<SelectionApp>;
    }
  | {
      type: 'TEXT';
      title?: string;
      text: string;
    }
  | {
      type: 'SCREENSHOTS';
      screenshots: Array<ScreenshotsType>;
    }
  | {
      type: 'EVENTS';
      events: Array<EventType>;
    }
);

export type AppsSelection = {
  id: string;
  title?: {
    value: string;
  };
  subtitle?: {
    value: string;
  };
  imageUrl?: string;
  type?: 'WITH_COVER_APP' | 'WITHOUT_COVER' | 'WITH_COVER' | 'WITH_EVENTS';
  displayType?: 'GRID' | 'RAIL' | 'LIST';
  content: Array<AppsSelectionContent>;
};

export type AppsSelectionWithoutContent = Omit<AppsSelection, 'content'>;

export type EventsSelectionType = {
  contentId: string;
  events: Array<EventType>;
  type: 'EVENTS';
};

export type EventType = {
  id: number;
  name: EventColoredContentType;
  shortDescription: EventColoredContentType;
  description: EventColoredContentType;
  bannerUrl: string;
  labels: Array<{text: EventColoredContentType; color: string}>;
  backgroundColor: string;
  appInfo: RecommendedApp;
  appDownloadTitle?: string;
};

export type EventColoredContentType = {value: string; color: string};

export type EventsContent = {
  type: 'EVENTS';
  events: Array<EventType>;
  contentId: string;
};

export type AppsSelectionWithEvents = Omit<AppsSelection, 'content'> & {
  content: Array<EventsContent>;
};

export type AllAppsSelections = {
  elements: Array<{
    selection: {
      id: string;
      content: {
        contentId: string;
      };
      type: 'WITH_COVER_APP' | 'WITHOUT_COVER' | 'WITH_COVER' | 'WITH_EVENTS';
    };
  }>;
};

export type VideoType = {
  host: string;
  url: string;
  previewUrl: string;
  previewUid: string;
  id: string;
  ownerVkId: string;
  orientation: 'LANDSCAPE' | 'PORTRAIT';
};

export type AppsSelectionResponse = Response<AppsSelection>;
export type AllAppsSelectionsResponse = Response<AllAppsSelections>;

export type PackageNamesResponse = {
  content: Array<{packageName: string}>;
} & PaginatedBaseResponse;

export type AppByPackageName = {
  versionId: number;
  id: number;
  name: string;
  packageName: string;
  description: string;
  iconUrl: string;
  appType: AppType;
  rating: {
    average: number;
    votes: number;
  } | null;
  labelIds: Array<number>;
};
